import { ShieldCheck, Activity, GitBranch } from "lucide-react";
import { cn } from "@/lib/utils";

type Tone = "verified" | "reviewed" | "unverified" | "insufficient" | "neutral";

interface Props {
  /** Numeric score, typically 0-100. */
  score: number;
  /** Optional band label (verified / reviewed / etc.). */
  band?: string | null;
  /** Override the tone derived from the score. */
  tone?: Tone;
  /** Compact variant — smaller text, no "How we score" sub-card. */
  compact?: boolean;
  className?: string;
}

function bandFromScore(score: number): { label: string; tone: Tone } {
  if (score >= 80) return { label: "Verified", tone: "verified" };
  if (score >= 60) return { label: "Reviewed", tone: "reviewed" };
  if (score >= 30) return { label: "Unverified", tone: "unverified" };
  return { label: "Insufficient", tone: "insufficient" };
}

const TONE_STYLES: Record<Tone, { ring: string; bg: string; text: string; dot: string }> = {
  verified:    { ring: "ring-emerald-500/30", bg: "bg-emerald-500/10", text: "text-emerald-400", dot: "bg-emerald-400" },
  reviewed:    { ring: "ring-amber-500/30",   bg: "bg-amber-500/10",   text: "text-amber-400",   dot: "bg-amber-400" },
  unverified:  { ring: "ring-red-500/30",     bg: "bg-red-500/10",     text: "text-red-400",     dot: "bg-red-400" },
  insufficient:{ ring: "ring-slate-500/30",   bg: "bg-slate-500/10",   text: "text-slate-400",   dot: "bg-slate-400" },
  neutral:     { ring: "ring-slate-500/30",   bg: "bg-slate-500/10",   text: "text-slate-300",   dot: "bg-slate-400" },
};

/**
 * Score readout used by every verification result surface.
 *
 * Shows the numeric score (0-100) with a band label in the matching tone, plus
 * a small "How we score" card explaining the three input categories at a high
 * level. Used on the public /verify page, the landing TryItNowSection result,
 * and the dashboard CarrierDetail / BrokerDetail pages.
 *
 * Pass `compact` to drop the explainer and just render the badge — useful for
 * inline or list contexts.
 */
export function ScoreExplainer({ score, band, tone, compact, className }: Props) {
  const auto = bandFromScore(score);
  const finalTone = tone ?? auto.tone;
  const styles = TONE_STYLES[finalTone];
  const label = band ?? auto.label;
  const clamped = Math.max(0, Math.min(100, Math.round(score)));

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {/* Score badge */}
      <div
        className={cn(
          "flex items-center gap-3 rounded-xl px-4 py-3",
          "ring-1",
          styles.ring,
          styles.bg,
        )}
      >
        <div className="flex items-baseline gap-1.5">
          <span className={cn("text-3xl font-bold tabular-nums leading-none", styles.text)}>
            {clamped}
          </span>
          <span className="text-xs text-muted-foreground tabular-nums">/100</span>
        </div>
        <div className="h-8 w-px bg-border/50" />
        <div className="flex items-center gap-2">
          <span className={cn("h-1.5 w-1.5 rounded-full", styles.dot)} />
          <span className={cn("text-sm font-semibold uppercase tracking-wider", styles.text)}>
            {label}
          </span>
        </div>
      </div>

      {/* How we score */}
      {!compact && (
        <div className="rounded-xl border border-border/60 bg-muted/30 p-3">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground mb-2">
            How we score
          </p>
          <ul className="space-y-1.5 text-xs text-foreground/80">
            <li className="flex items-start gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-400 mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">Authority &amp; insurance.</strong>{" "}
                Active operating authority, BMC-84 bond status, surety amount, insurance on file.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Activity className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">Safety &amp; recency.</strong>{" "}
                FMCSA safety rating, CSA scores, MCS-150 freshness, age of authority, fleet size sanity.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <GitBranch className="h-3.5 w-3.5 text-purple-400 mt-0.5 shrink-0" />
              <span>
                <strong className="text-foreground">Identity signals.</strong>{" "}
                Address fingerprint, contact correlation, MC re-issue history, chameleon-carrier detection.
              </span>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
